import { Badge } from '@/components/ui/Badge'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { formatAmount, formatDate, formatDateRange } from '@/lib/venue/format'
import type { VenueSettlementRevenueView } from '@/lib/venue/settlementTypes'
import {
  SETTLEMENT_CYCLE_STATUS_TONE,
  confirmedAmountNoticeText,
  partialCycleNoticeText,
} from '@/lib/venue/settlementViewModel'

/**
 * 매출 현황 요약 — 홈·매출 현황 화면 공용(설계 §2 자율 결정: 두 화면이 같은 카드를 재사용한다).
 *
 * 🚨 `revenue`(기간 축)와 `settlement`(회차 축)를 **별도 섹션**으로만 렌더한다 — 두 블록의 숫자를
 * 더하거나 비교하는 코드를 이 컴포넌트에 두지 않는다(api-spec §1 규약을 "만들 수 있는 코드가 없음"으로 강제).
 */
export function SettlementRevenueSummaryCard({ data }: { data: VenueSettlementRevenueView }) {
  const partialNotice = partialCycleNoticeText(data.settlement)
  const confirmedNotice = confirmedAmountNoticeText(data.settlement)

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title={`${data.presetLabel} 매출`}
          description={formatDateRange(data.from, data.to)}
        />
        <CardBody>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs text-slateish-600">총 예약액</dt>
              <dd className="mt-1 text-lg font-semibold text-ink-700">
                {formatAmount(data.revenue.completedReservationAmount)}
              </dd>
              <p className="mt-0.5 text-xs text-slateish-400">
                이용 완료 {data.revenue.completedReservationCount.toLocaleString('ko-KR')}건
              </p>
            </div>
            <div>
              <dt className="text-xs text-slateish-600">취소·환불 차감액</dt>
              <dd className="mt-1 text-lg font-semibold text-danger-500">
                -{formatAmount(data.revenue.refundDeductedAmount)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-slateish-600">중개 수수료</dt>
              <dd className="mt-1 text-sm text-ink-700">
                -{formatAmount(data.revenue.commissionAmount)}
                <span className="ml-1 text-xs text-slateish-400">
                  ({data.revenue.commissionRatePercent}%)
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-xs text-slateish-600">순 매출</dt>
              <dd className="mt-1 text-sm font-semibold text-ink-700">
                {formatAmount(data.revenue.netAmount)}
              </dd>
            </div>
          </dl>
          {/* 🚨 참고 표시 전용 — 어떤 합계에도 더하지 않는다(정산 대상이 아닌 구장 직수납액). */}
          {data.revenue.onsitePaidAmount > 0 ? (
            <p className="mt-3 text-xs text-slateish-400">
              참고 · 현장 수납액 {formatAmount(data.revenue.onsitePaidAmount)}(정산 대상 아님)
            </p>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="정산" description="회차(월 정산) 기준 확정 금액과 다음 정산 예정 내역입니다." />
        <CardBody>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs text-slateish-600">정산 확정 금액</dt>
              <dd className="mt-1 text-lg font-semibold text-ink-700">
                {formatAmount(data.settlement.confirmedAmount)}
              </dd>
              {confirmedNotice ? <p className="mt-1 text-xs text-slateish-400">{confirmedNotice}</p> : null}
            </div>
            <div>
              <dt className="text-xs text-slateish-600">
                다음 정산 예정{' '}
                <Badge tone={SETTLEMENT_CYCLE_STATUS_TONE[data.settlement.next.status]}>
                  {data.settlement.next.statusLabel}
                </Badge>
              </dt>
              <dd className="mt-1 text-lg font-semibold text-ink-700">
                {formatAmount(data.settlement.next.estimatedPayoutAmount)}
              </dd>
              <p className="mt-0.5 text-xs text-slateish-400">{formatDate(data.settlement.next.payoutDate)} 지급</p>
            </div>
          </dl>
          {partialNotice ? <p className="mt-3 text-xs text-slateish-400">{partialNotice}</p> : null}
        </CardBody>
      </Card>
    </div>
  )
}
